import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Trophy, Users, FileText, Loader2 } from "lucide-react";
import api from "@/lib/api";
import HorseIcon from "@/components/icons/HorseIcon";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { recordTap } from "@/lib/taps";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export function SearchButton() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <>
      <button
        type="button"
        data-testid="header-search-btn"
        onClick={() => setOpen(true)}
        title="Search horses, shows, owners, invoices (⌘K)"
        className="h-8 w-8 rounded-md border border-slate-600 text-slate-200 md:border-zinc-300 md:text-slate-600 hover:opacity-75 inline-flex items-center justify-center transition-colors"
      >
        <Search className="w-4 h-4" />
      </button>
      {open && <SearchSheet onClose={() => setOpen(false)} />}
    </>
  );
}

function SearchSheet({ onClose }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const navigate = useNavigate();
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 220); return () => clearTimeout(t); }, [q]);
  const { data, isFetching } = useQuery({
    queryKey: ["search", debounced],
    queryFn: async () => (await api.get(`/search?q=${encodeURIComponent(debounced)}`)).data,
    enabled: debounced.length >= 2,
    staleTime: 15000,
  });
  const go = (key, to) => { recordTap(key); onClose(); navigate(to); };
  const groups = [
    { key: "horses", label: "Horses", icon: HorseIcon, items: data?.horses || [],
      title: (h) => h.barn_name, sub: (h) => [h.show_name, h.owner].filter(Boolean).join(" · "), to: (h) => `/horses?horse=${h.id}` },
    { key: "shows", label: "Shows", icon: Trophy, items: data?.shows || [],
      title: (s) => s.name, sub: (s) => [s.start_date, s.location].filter(Boolean).join(" · "), to: (s) => `/shows/${s.id}` },
    { key: "owners", label: "Owners", icon: Users, items: data?.owners || [],
      title: (o) => o.owner, sub: () => "Account · invoices & payments", to: (o) => `/invoices?tab=accounts&owner=${encodeURIComponent(o.owner)}` },
    { key: "invoices", label: "Invoices", icon: FileText, items: data?.invoices || [],
      title: (i) => `${i.owner} — ${i.show_name || i.label || "Invoice"}`,
      sub: (i) => `${i.status}${i.paid_in_full ? " · paid" : i.balance > 0 ? ` · ${money(i.balance)} due` : ""} · ${money(i.total)}`,
      to: (i) => `/invoices?open=${i.id}` },
  ].filter((g) => g.items.length);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg p-0 gap-0 top-[8vh] translate-y-0 max-h-[84vh] overflow-hidden flex flex-col" data-testid="search-sheet">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200">
          {isFetching ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <Search className="w-4 h-4 text-slate-400" />}
          <input
            autoFocus
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search horses, shows, owners, invoices…"
            className="flex-1 bg-transparent outline-none text-base placeholder:text-slate-400"
          />
        </div>
        <div className="overflow-y-auto p-2" data-testid="search-results">
          {debounced.length < 2 ? (
            <p className="px-3 py-6 text-sm text-slate-400 text-center">Type at least two letters — a horse, a show, an owner…</p>
          ) : !isFetching && total === 0 ? (
            <p className="px-3 py-6 text-sm text-slate-400 text-center" data-testid="search-empty">Nothing matches “{debounced}”.</p>
          ) : groups.map((g) => (
            <div key={g.key} className="mb-2" data-testid={`search-group-${g.key}`}>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-400 flex items-center gap-1.5">
                <g.icon className="w-3 h-3" /> {g.label}
              </div>
              {g.items.map((it, i) => (
                <button key={`${g.key}-${i}`} type="button" onClick={() => go(`search:${g.key}`, g.to(it))}
                  data-testid={`search-hit-${g.key}-${i}`}
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-amber-50 active:bg-amber-100 flex items-baseline justify-between gap-3 min-h-[44px]">
                  <span className="font-medium text-slate-900 truncate">{g.title(it)}</span>
                  <span className="text-xs text-slate-500 shrink-0 truncate max-w-[45%]">{g.sub(it)}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
